import { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { sharingSteps } from './ProjectSharing';
import { ProjectSharingHeader } from './ProjectSharingHeader';
import styles from './project-sharing.module.less';
import { trackEvent } from '../../support-and-tracking/analytics-actions';
import { ProjectSharingFooter } from './ProjectSharingFooter';
import { MarkAsCompleteButton } from './ProjectSharingMarkAsComplete';
import { fetchShortlinkForProject, saveCustomUrlAction } from '../../shortlink/shortlink-actions';
import { getLinkForProject } from '../../shortlink/shortlink-reducer';
import { getFormActions } from '../../utils/form-helpers';
import shortlinkFormConfig from '../../shortlink/shortlink-form-config';
import { getUrlFromShortlink } from '../../shortlink/shortlink-model';
import RoundedButtonPrimary from '../../components/button/rounded-button-primary/RoundedButtonPrimary';
import withRouter from '../../components/hooks/withRouter';
import Input2 from '../../components/input/input-2/Input2';

const { setFormField, clearAll } = getFormActions(shortlinkFormConfig);

class ProjectSharingCustomUrl extends Component {
    static fetchData(dispatch, params) {
        const { projectId } = params;
        return dispatch(fetchShortlinkForProject(projectId, true));
    }

    saveCustomUrl() {
        const { dispatch, shortlinkForm, projectId } = this.props;
        dispatch(saveCustomUrlAction({ shortlink: shortlinkForm.shortlink, projectId }));
    }

    componentDidMount() {
        const { dispatch, params } = this.props;
        ProjectSharingCustomUrl.fetchData(dispatch, params);
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.saveCustomUrlSuccess && this.props.saveCustomUrlSuccess) {
            this.setCustomUrlStepAsComplete();
        }
    }

    componentWillUnmount() {
        const { dispatch } = this.props;
        dispatch(clearAll());
    }

    setCustomUrlStepAsComplete() {
        const { projectId, setSharingStepComplete } = this.props;
        setSharingStepComplete(sharingSteps.customUrl);
        trackEvent('shareguide', 'step-complete', {
            activity: 'customUrl',
            'project-id': projectId,
        });
    }

    onShortlinkInputChange(e) {
        const { dispatch } = this.props;
        const value = e.target.value.toLowerCase();
        const cleanedValue = value.replace(/[^\w-æøå]/, '');
        dispatch(setFormField('shortlink', cleanedValue));
    }

    onShortlinkInputBlur(e) {
        const { dispatch } = this.props;
        e.target.value && dispatch(setFormField('shortlink', e.target.value, true));
    }

    render() {
        const { projectId, completed, showHeaderAndFooter, shortlink, shortlinkForm, errors } = this.props;
        const footerBackLink = `/project/${projectId}/sharing`;
        const footerBackLinkText = 'Oversikt';
        const footerForwardLink = `/project/${projectId}/sharing/2`;
        const footerForwardLinkText = 'Del på Facebook';

        const title = shortlink ? 'Lenken din er aktiv 🎉' : 'Lag en personlig lenke som er lett å dele';

        return (
            <>
                {showHeaderAndFooter ? (
                    <ProjectSharingHeader
                        title={title}
                        backLink={`/project/${projectId}/sharing`}
                        backLinkText="Tilbake til delingspanelet"
                    />
                ) : (
                    <ProjectSharingHeader title={title} showLinks={false} />
                )}
                <div className={styles.contentPanel}>
                    {shortlink ? (
                        <>
                            <div className={styles.customUrlEnterValuePanel}>
                                <Input2
                                    readOnly={true}
                                    value={getUrlFromShortlink(shortlink)}
                                    fieldName={'shortLink'}
                                />
                            </div>
                            {showHeaderAndFooter ? (
                                <div className={styles.customUrlDescription}>
                                    <p>Yay! Den superenkle lenken er klar til å deles.</p>
                                    <p>PS: Du kan ikke endre lenken nå som den er aktiv.</p>
                                </div>
                            ) : (
                                <div className={styles.customUrlDescription}>
                                    Fortsett til delingspanel for å dele spleisen din på Facebook og i ditt nettverk.
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className={styles.customUrlEnterValuePanel}>
                                <label htmlFor="shortlinkInput" className={styles.customUrlHost}>
                                    spleis.no/
                                </label>
                                <Input2
                                    id="shortlinkInput"
                                    autoCapitalize="none"
                                    fieldName={'shortlink'}
                                    className={styles.customUrlTextFieldEditable}
                                    onChange={(e) => this.onShortlinkInputChange(e)}
                                    onBlur={(e) => this.onShortlinkInputBlur(e)}
                                    value={shortlinkForm.shortlink}
                                    errors={errors}
                                    autoFocus
                                />
                            </div>
                            <div className={styles.customUrlDescription}>
                                Lenken bør være lett å dele, både muntlig og skriftlig, som f.eks «hagecrew», «byåsen»
                                eller «hanna30».
                            </div>
                            <div className={styles.shareButton}>
                                <RoundedButtonPrimary
                                    className={styles.saveButton}
                                    onClick={() => this.saveCustomUrl()}
                                >
                                    Lagre lenke
                                </RoundedButtonPrimary>
                            </div>
                            <div className={styles.customUrlDescription}>
                                Obs! Du kan ikke endre lenken i etterkant.
                            </div>
                        </>
                    )}
                    {showHeaderAndFooter && (
                        <MarkAsCompleteButton
                            completed={completed}
                            onClick={() => {
                                this.setCustomUrlStepAsComplete();
                            }}
                        />
                    )}
                </div>
                {showHeaderAndFooter ? (
                    <ProjectSharingFooter
                        backLink={footerBackLink}
                        backLinkText={footerBackLinkText}
                        forwardLink={footerForwardLink}
                        forwardLinkText={footerForwardLinkText}
                    />
                ) : shortlink ? (
                    <div className={styles.shareButton}>
                        <RoundedButtonPrimary onClick={() => this.props.navigate('./')}>
                            Fortsett til delingspanelet
                        </RoundedButtonPrimary>
                    </div>
                ) : (
                    <div className={styles.customUrlSkip}>
                        <Link to={'./'}>Hopp over!</Link>
                    </div>
                )}
            </>
        );
    }
}

ProjectSharingCustomUrl.propTypes = {
    dispatch: PropTypes.func.isRequired,
    showHeaderAndFooter: PropTypes.bool,
    shortlink: PropTypes.string,
    projectId: PropTypes.string,
    shortlinkForm: PropTypes.object,
    saveCustomUrlSuccess: PropTypes.bool,
    errors: PropTypes.object,
    setSharingStepComplete: PropTypes.func.isRequired,
    completed: PropTypes.bool,
};

ProjectSharingCustomUrl.defaultProps = {
    showHeaderAndFooter: true,
    shortlink: '',
    projectId: '',
    saveCustomUrlSuccess: false,
    shortlinkForm: {},
    errors: {},
};

const mapStateToProps = (state, props) => {
    const projectId = props.params?.projectId;

    return {
        projectId,
        shortlink: getLinkForProject({ state, projectId }),
        shortlinkForm: state.shortlinkForm,
        errors: state.shortlinkForm.errors || { shortlink: state.shortlinks.errors?.global },
        saveCustomUrlSuccess: state.shortlinks.saveCustomUrlSuccess,
    };
};

export default withRouter(connect(mapStateToProps)(ProjectSharingCustomUrl));
